// React imports
import React, { useEffect, useState } from "react";

// External library imports
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PersonIcon from "@mui/icons-material/Person";
import RateReviewIcon from "@mui/icons-material/RateReview";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import PageTitle from "../../../components/Page Component/PageTitle";
import { RootState } from "../../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../../store/actions";
import {
  ToastVariant,
  useCustomToast,
} from "../../../components/Methods/SnackBar";
import {
  managerTeamKRAsUrl,
  submitManagerReviewUrl,
  saveManagerReviewDraftUrl,
  managerContributionsUrl,
} from "../../../Service/Apis";
import {
  getStatusChip,
  formatDate,
  calculateWeightedRating,
  KRAStatus,
  getPeriodShortLabel,
  getPeriodDaysWarningColor,
  formatRatingLabel,
} from "../utils";
import RatingInput from "./components/RatingInput";
import SkillDocumentCommentBox from "../../../components/SkillDocumentCommentBox/SkillDocumentCommentBox";
import RichTextDisplay from "../../../components/SkillDocumentCommentBox/RichTextDisplay";
import { isRichTextEmpty } from "../../../components/SkillDocumentCommentBox/utils";

interface KPIRating {
  kpi_id: string;
  question: string;
  weightage: number;
  rating_type?: 'numerical' | 'likert' | 'boolean';
  self_rating: number | null;
  manager_rating: number | null;
  self_comments?: string | null;
  manager_comments?: string | null;
  // Completed dual-role: both M1 and M2 per-KPI data
  m1_manager_rating?: number | null;
  m1_manager_comments?: string | null;
  m2_manager_rating?: number | null;
  m2_manager_comments?: string | null;
}

interface KRASectionRating {
  section_id: string;
  section: string;
  weightage: number;
  questions: KPIRating[];
}

interface EmployeeKRA {
  employee_kra_id: string;
  employee_name: string;
  employee_code: string;
  employee_email: string;
  form_name: string;
  designation: string;
  assigned_date: string;
  accepted_date?: string | null;
  self_review_submitted_date?: string | null;
  manager_review_submitted_date?: string | null;
  status: KRAStatus;
  sections: KRASectionRating[];
  manager_overall_rating: number | null;
  self_review_overall_rating: number | null;
  self_review_overall_comments?: string;
  manager_overall_comments?: string;
  review_period: string;
  period_start_date: string;
  period_end_date: string;
  days_remaining: number;
  is_period_expired: boolean;
  // Dual-manager fields
  manager_1?: string | null;
  manager_1_name?: string | null;
  manager_1_overall_rating?: number | null;
  manager_1_review_submitted_date?: string | null;
  manager_2?: string | null;
  manager_2_name?: string | null;
  manager_2_overall_rating?: number | null;
  manager_2_review_submitted_date?: string | null;
  // Per-role project context (populated when M1 and M2 assignments differ by project)
  m1_project_name?: string | null;
  m2_project_name?: string | null;
  // Set when this manager completed both M1 and M2 reviews for the same KRA
  is_completed_dual_role?: boolean;
  manager_1_overall_comments?: string | null;
  manager_2_overall_comments?: string | null;
}

/**
 * A single row in the Direct Reports Contribution table (Hierarchical KPI
 * Linkage extension, spec S-21): Name, Child KPI ID, Weight, Child Rating,
 * Child %Contribution, vs Threshold.
 */
interface DirectReportContributionRow {
  employee_kra_id: string;
  employee_name: string;
  child_kpi_id: string;
  child_kpi_code: string | null;
  child_kpi_title: string;
  parent_kpi_id: string;
  parent_kpi_code: string | null;
  weight: number;
  child_rating: number;
  threshold: number | null;
  derived_percentage: number | null;
}

const ManagerKRAReview: React.FC = () => {
  // useState declarations
  const [teamKRAs, setTeamKRAs] = useState<EmployeeKRA[]>([]);
  const [directReportContributions, setDirectReportContributions] = useState<{
    as_manager_1: DirectReportContributionRow[];
    as_manager_2: DirectReportContributionRow[];
  }>({ as_manager_1: [], as_manager_2: [] });
  const [contributionsExpanded, setContributionsExpanded] = useState<boolean>(false);
  const [filteredKRAs, setFilteredKRAs] = useState<EmployeeKRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState<boolean>(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeKRA | null>(null);
  const [structureDialogOpen, setStructureDialogOpen] = useState<boolean>(false);
  const [structureEmployee, setStructureEmployee] = useState<EmployeeKRA | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [savingDraft, setSavingDraft] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showCommentsError, setShowCommentsError] = useState<boolean>(false);
  const [showKPICommentsError, setShowKPICommentsError] = useState<boolean>(false);
  
  // Sorting state
  const [sortField, setSortField] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Expanded rows for M1/M2 detail panels
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Controls what the review dialog shows: M1-only, M2-only, or the full combined view
  const [dialogMode, setDialogMode] = useState<"m1" | "m2" | "full">("full");

  // Other constants and hooks
  const showToast = useCustomToast();

  const currentUserId = localStorage.getItem("id");

  const getManagerLevel = (kra: EmployeeKRA): "M1" | "M2" | null => {
    if (!currentUserId) return null;
    // Same person holds both M1 and M2 (dual-role, different projects).
    // Mirror the backend auto-routing logic: M1 first, then M2.
    if (
      kra.manager_1 &&
      kra.manager_2 &&
      kra.manager_1 === kra.manager_2 &&
      kra.manager_1 === currentUserId
    ) {
      return kra.manager_1_review_submitted_date ? "M2" : "M1";
    }
    if (kra.manager_1 && kra.manager_1 === currentUserId) return "M1";
    if (kra.manager_2 && kra.manager_2 === currentUserId) return "M2";
    return "M1"; // legacy fallback
  };

  const hasCurrentManagerSubmitted = (kra: EmployeeKRA): boolean => {
    // Dual-role: only fully done when both M1 and M2 slots are submitted.
    if (
      kra.manager_1 &&
      kra.manager_2 &&
      kra.manager_1 === kra.manager_2 &&
      kra.manager_1 === currentUserId
    ) {
      return !!kra.manager_1_review_submitted_date && !!kra.manager_2_review_submitted_date;
    }
    const level = getManagerLevel(kra);
    if (level === "M1") return !!kra.manager_1_review_submitted_date;
    if (level === "M2") return !!kra.manager_2_review_submitted_date;
    return !!kra.manager_review_submitted_date;
  };

  // True when this manager held both M1 and M2 roles and submitted both reviews.
  const isCompletedDualRole = (kra: EmployeeKRA): boolean =>
    !!(kra.is_completed_dual_role && kra.manager_1 === currentUserId);

  // True when the KRA has separate M1 and M2 managers assigned.
  const hasDualManagers = (kra: EmployeeKRA): boolean =>
    !!(kra.manager_1 && kra.manager_2);

  const toggleRowExpand = (kraId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(kraId)) {
        next.delete(kraId);
      } else {
        next.add(kraId);
      }
      return next;
    });
  };

  // Returns a status chip for the table row.
  // When the KRA has two *different* managers and is awaiting manager review,
  // the label is refined to indicate which manager(s) still need to submit.
  const getDualManagerStatusChip = (kra: EmployeeKRA): JSX.Element => {
    if (
      kra.status === "awaiting_manager_review" &&
      kra.manager_1 &&
      kra.manager_2 &&
      kra.manager_1 !== kra.manager_2
    ) {
      const m1Submitted = !!kra.manager_1_review_submitted_date;
      const m2Submitted = !!kra.manager_2_review_submitted_date;

      let label: string;
      if (!m1Submitted && !m2Submitted) {
        label = "Awaiting Managers' Review";
      } else if (m1Submitted && !m2Submitted) {
        label = "Awaiting 2nd Manager's Review";
      } else if (!m1Submitted && m2Submitted) {
        label = "Awaiting 1st Manager's Review";
      } else {
        return getStatusChip(kra.status);
      }

      return (
        <Chip
          label={label}
          color="info"
          icon={<RateReviewIcon sx={{ fontSize: 16 }} />}
          size="small"
          sx={{ fontWeight: "600" }}
        />
      );
    }

    return getStatusChip(kra.status);
  };

  // When M1 and M2 are the same person and both ratings exist, return their average.
  // Otherwise return the standard manager_overall_rating.
  const getEffectiveManagerRating = (kra: EmployeeKRA): number | null => {
    if (
      kra.manager_1 &&
      kra.manager_2 &&
      kra.manager_1 === kra.manager_2 &&
      kra.manager_1_overall_rating != null &&
      kra.manager_2_overall_rating != null
    ) {
      return (Number(kra.manager_1_overall_rating) + Number(kra.manager_2_overall_rating)) / 2;
    }
    return kra.manager_overall_rating != null ? Number(kra.manager_overall_rating) : null;
  };

  useEffect(() => {
    applyFiltersAndSort();
  }, [teamKRAs, statusFilter, searchQuery, sortField, sortOrder]);
  // Redux variables
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const fetchTeamKRAs = async () => {
    setLoading(true);
    try {
      const response = await dispatch(fetchData(managerTeamKRAsUrl));
      
      if (response && Array.isArray(response)) {
        setTeamKRAs(response);
      } else {
        setTeamKRAs([]);
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to load team KRAs";
      showToast(errorMessage, "error", true);
      setTeamKRAs([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...teamKRAs];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((kra) => kra.status === statusFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (kra) =>
          kra.employee_name.toLowerCase().includes(query) ||
          kra.employee_code.toLowerCase().includes(query) ||
          kra.employee_email.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    if (sortField) {
      filtered = filtered.sort((a, b) => {
        let aValue: string | number | undefined;
        let bValue: string | number | undefined;
        
        switch (sortField) {
          case "employee_name":
            aValue = a.employee_name?.toLowerCase() || "";
            bValue = b.employee_name?.toLowerCase() || "";
            break;
          case "form_name":
            aValue = a.form_name?.toLowerCase() || "";
            bValue = b.form_name?.toLowerCase() || "";
            break;
          case "review_period":
            // Map period to sortable values
            const periodOrder: { [key: string]: number } = {
              'monthly': 1,
              'quarterly': 2,
              'half_yearly': 3,
              'yearly': 4
            };
            aValue = periodOrder[a.review_period] || 0;
            bValue = periodOrder[b.review_period] || 0;
            break;
          case "self_review_overall_rating":
            aValue = a.self_review_overall_rating ?? -1;
            bValue = b.self_review_overall_rating ?? -1;
            break;
          case "manager_overall_rating":
            aValue = a.manager_overall_rating ?? -1;
            bValue = b.manager_overall_rating ?? -1;
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredKRAs(filtered);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field with ascending order
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleReviewClick = (employee: EmployeeKRA, mode: "m1" | "m2" | "full" = "full") => {
    setSelectedEmployee(JSON.parse(JSON.stringify(employee)));
    setDialogMode(mode);
    setReviewDialogOpen(true);
  };

  const handleViewStructureClick = (employee: EmployeeKRA) => {
    setStructureEmployee(employee);
    setStructureDialogOpen(true);
  };

  const handleRatingChange = (
    sectionIndex: number,
    questionIndex: number,
    rating: number
  ) => {
    if (!selectedEmployee) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedEmployee)) as EmployeeKRA;
    updated.sections[sectionIndex].questions[questionIndex].manager_rating = rating;
    setSelectedEmployee(updated);
  };

  const handleKPICommentChange = (
    sectionIndex: number,
    questionIndex: number,
    comment: string
  ) => {
    if (!selectedEmployee) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedEmployee)) as EmployeeKRA;
    updated.sections[sectionIndex].questions[questionIndex].manager_comments = comment;
    setSelectedEmployee(updated);
  };

  const handleOverallCommentsChange = (comments: string) => {
    if (!selectedEmployee) return;

    // Deep copy to avoid state mutation
    const updated = JSON.parse(JSON.stringify(selectedEmployee)) as EmployeeKRA;
    updated.manager_overall_comments = comments;
    setSelectedEmployee(updated);
    // Clear error when user starts typing
    if (showCommentsError && comments.trim() !== "") {
      setShowCommentsError(false);
    }
  };

  const calculateAverageRating = () => {
    if (!selectedEmployee) return 0;
    return calculateWeightedRating(selectedEmployee.sections, 'manager_rating');
  };

  const getAverageRatingData = (rating: number | string) => {
    const avg = Number(rating);
    return {
      average: avg.toFixed(2),
      percentage: ((avg / 5) * 100).toFixed(1),
    };
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!selectedEmployee) return;

    if (savingDraft) return; // Prevent double save

    setSavingDraft(true);
    try {
      const sectionsData = selectedEmployee.sections.map((section) => ({
        section_id: section.section_id,
        questions: section.questions.map((q) => ({
          kpi_id: q.kpi_id,
          manager_rating: q.manager_rating,
          manager_comments: q.manager_comments || "",
        })),
      }));

      const payload = {
        sections: sectionsData,
        manager_overall_rating: calculateAverageRating(),
        manager_overall_comments: selectedEmployee.manager_overall_comments || "",
      };

      await axios.post(
        saveManagerReviewDraftUrl(selectedEmployee.employee_kra_id),
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      showToast("Draft saved successfully! Refreshing data...", "success", true);
      
      // Refresh the data to show saved draft
      await fetchTeamKRAs();
      
      // Close dialog to show updated data
      setReviewDialogOpen(false);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || "Failed to save draft";
      showToast(errorMessage, "error", true);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedEmployee) return;

    // Prevent double submission
    if (submitting) return;

    // Validation: Check if all KPIs are rated (null/undefined check only, 0 is valid)
    let allRated = true;
    let unratedKPIs: string[] = [];
    
    selectedEmployee.sections.forEach((section) => {
      section.questions.forEach((kpi) => {
        if (kpi.manager_rating === null || kpi.manager_rating === undefined) {
          allRated = false;
          unratedKPIs.push(kpi.question.substring(0, 50) + "...");
        }
      });
    });

    if (!allRated) {
      showToast(
        `Please rate all KPIs before submitting. Missing ${unratedKPIs.length} rating(s).`,
        "error",
        true
      );
      return;
    }

    // Validation: Check if all individual KPI comments are provided
    let allKPICommented = true;
    let uncommentedKPIs: string[] = [];

    selectedEmployee.sections.forEach((section) => {
      section.questions.forEach((kpi) => {
        if (isRichTextEmpty(kpi.manager_comments)) {
          allKPICommented = false;
          uncommentedKPIs.push(kpi.question.substring(0, 50) + "...");
        }
      });
    });

    if (!allKPICommented) {
      setShowKPICommentsError(true);
      showToast(
        `Please add comments for all KPIs before submitting. Missing ${uncommentedKPIs.length} comment(s).`,
        "error",
        true
      );
      return;
    }
    setShowKPICommentsError(false);

    // Validation: Check if overall comments are provided
    if (isRichTextEmpty(selectedEmployee.manager_overall_comments)) {
      setShowCommentsError(true);
      showToast(
        "Please provide overall comments before submitting your review.",
        "error",
        true
      );
      return;
    }
    setShowCommentsError(false);

    // Auto-calculate overall rating
    const overallRating = calculateAverageRating();
    
    // Round to 2 decimal places to match backend DecimalField(max_digits=3, decimal_places=2)
    const roundedRating = Math.round(overallRating * 100) / 100;

    setSubmitting(true);
    try {
      const sectionsData = selectedEmployee.sections.map((section) => ({
        section_id: section.section_id,
        questions: section.questions.map((kpi) => ({
          kpi_id: kpi.kpi_id,
          manager_rating: kpi.manager_rating || 0,
          manager_comments: kpi.manager_comments || "",
        })),
      }));

      const payload = {
        sections: sectionsData,
        manager_overall_rating: roundedRating,
        manager_overall_comments: selectedEmployee.manager_overall_comments || "",
      };

      const token = localStorage.getItem("token");
      const response = await axios.post(
        submitManagerReviewUrl(selectedEmployee.employee_kra_id),
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data && response.data.success) {
        // Update local state with response data
        setTeamKRAs((prev) =>
          prev.map((kra) =>
            kra.employee_kra_id === selectedEmployee.employee_kra_id
              ? response.data.data
              : kra
          )
        );

        showToast("Review submitted successfully!", "success", true);
        setReviewDialogOpen(false);
        setSelectedEmployee(null);
      } else {
        throw new Error(response.data?.message || "Failed to submit review");
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.error || axiosError.message || "Failed to submit review. Please try again.";
      showToast(errorMessage, "error", true);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchDirectReportContributions = async () => {
    try {
      const response = await dispatch(fetchData(managerContributionsUrl));
      if (response) {
        setDirectReportContributions({
          as_manager_1: response.as_manager_1 || [],
          as_manager_2: response.as_manager_2 || [],
        });
      }
    } catch (error: unknown) {
      // Non-critical, informational data only - fail silently.
    }
  };

  // useEffect hooks
  useEffect(() => {
    fetchTeamKRAs();
    fetchDirectReportContributions();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [teamKRAs, statusFilter, searchQuery, sortField, sortOrder]);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  const rating = calculateAverageRating();
  const { average, percentage } = getAverageRatingData(rating);

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <PageTitle>Team KRA Review</PageTitle>
        </Box>
        <Typography variant="body1" color="black">
          Review and rate your team members' Key Responsibility Areas and Key Performance Indicators
        </Typography>
      </Box>

      {/* Direct Reports Contribution (Hierarchical KPI Linkage extension, spec S-21/S-22).
          Purely informational - does not affect rating entry below. Hidden entirely
          when no linkages/contributions exist for this manager's direct reports. */}
      {(directReportContributions.as_manager_1.length > 0 ||
        directReportContributions.as_manager_2.length > 0) && (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
            onClick={() => setContributionsExpanded((prev) => !prev)}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Direct Reports Contribution
            </Typography>
            <IconButton size="small">
              {contributionsExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          <Collapse in={contributionsExpanded}>
            {(["as_manager_1", "as_manager_2"] as const).map((slot) =>
              directReportContributions[slot].length > 0 ? (
                <Box key={slot} sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    {slot === "as_manager_1" ? "As Manager 1 (M1)" : "As Manager 2 (M2)"}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Child KPI ID</TableCell>
                          <TableCell align="right">Weight</TableCell>
                          <TableCell align="right">Child Rating</TableCell>
                          <TableCell align="right">Threshold</TableCell>
                          <TableCell align="right">Child %Contribution</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {directReportContributions[slot].map((row, idx) => (
                          <TableRow key={`${row.employee_kra_id}-${row.child_kpi_id}-${idx}`}>
                            <TableCell>{row.employee_name}</TableCell>
                            <TableCell>
                              <Tooltip title={row.child_kpi_title}>
                                <span>{row.child_kpi_code || row.child_kpi_id.slice(0, 8)}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">{row.weight}</TableCell>
                            <TableCell align="right">{row.child_rating.toFixed(2)}</TableCell>
                            <TableCell align="right">{row.threshold ?? "—"}</TableCell>
                            <TableCell align="right">
                              {row.derived_percentage != null
                                ? `${(row.derived_percentage * 100).toFixed(2)}%`
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ) : null
            )}
          </Collapse>
        </Paper>
      )}

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, code, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending Acceptance</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="awaiting_manager_review">Awaiting Manager Review</MenuItem>
                <MenuItem value="awaiting_hr_review">Awaiting HR Review</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Empty State */}
      {filteredKRAs.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <PersonIcon sx={{ fontSize: 80, color: "black", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No Team Members Found
          </Typography>
          <Typography variant="body2" color="black">
            {teamKRAs.length === 0
              ? "You don't have any team members with assigned KRAs yet."
              : "No team members match your search criteria."}
          </Typography>
        </Paper>
      ) : (
        /* Team Members Table */
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.100" }}>
                <TableCell sx={{ fontWeight: "bold", width: 32 }} />
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "employee_name"}
                    direction={sortField === "employee_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("employee_name")}
                  >
                    Employee
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "form_name"}
                    direction={sortField === "form_name" ? sortOrder : "asc"}
                    onClick={() => handleSort("form_name")}
                  >
                    KRA
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Assigned</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "review_period"}
                    direction={sortField === "review_period" ? sortOrder : "asc"}
                    onClick={() => handleSort("review_period")}
                  >
                    Period
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "self_review_overall_rating"}
                    direction={sortField === "self_review_overall_rating" ? sortOrder : "asc"}
                    onClick={() => handleSort("self_review_overall_rating")}
                  >
                    Self Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  <TableSortLabel
                    active={sortField === "manager_overall_rating"}
                    direction={sortField === "manager_overall_rating" ? sortOrder : "asc"}
                    onClick={() => handleSort("manager_overall_rating")}
                  >
                    Manager Rating
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredKRAs.map((employee) => (
                <React.Fragment key={employee.employee_kra_id}>
                <TableRow hover>
                  {/* Expand / collapse toggle — only shown when same person holds both M1 and M2 */}
                  <TableCell sx={{ width: 32, p: 0.5 }}>
                    {hasDualManagers(employee) && employee.manager_1 === employee.manager_2 && (
                      <Tooltip title={expandedRows.has(employee.employee_kra_id) ? "Hide M1/M2 details" : "Show M1/M2 details"}>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpand(employee.employee_kra_id)}
                          sx={{ color: "text.secondary" }}
                        >
                          {expandedRows.has(employee.employee_kra_id)
                            ? <KeyboardArrowUpIcon fontSize="small" />
                            : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: "600" }}>
                          {employee.employee_name}
                        </Typography>
                        {isCompletedDualRole(employee) ? (
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            <Chip
                              label={employee.m1_project_name ? `M1 · ${employee.m1_project_name}` : "M1"}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 18, fontSize: "0.6rem", fontWeight: "bold", "& .MuiChip-label": { px: 0.75, lineHeight: 1.3 } }}
                            />
                            <Chip
                              label={employee.m2_project_name ? `M2 · ${employee.m2_project_name}` : "M2"}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ height: 18, fontSize: "0.6rem", fontWeight: "bold", "& .MuiChip-label": { px: 0.75, lineHeight: 1.3 } }}
                            />
                          </Box>
                        ) : getManagerLevel(employee) && (() => {
                          const level = getManagerLevel(employee);
                          const isDualRole =
                            employee.manager_1 &&
                            employee.manager_2 &&
                            employee.manager_1 === employee.manager_2 &&
                            employee.manager_1 === currentUserId;
                          const projectLabel =
                            isDualRole
                              ? level === "M1"
                                ? employee.m1_project_name
                                : employee.m2_project_name
                              : null;
                          return (
                            <Chip
                              label={projectLabel ? `${level} · ${projectLabel}` : level}
                              size="small"
                              color={level === "M1" ? "primary" : "secondary"}
                              variant="outlined"
                              sx={{ height: 18, fontSize: "0.6rem", fontWeight: "bold", "& .MuiChip-label": { px: 0.75, lineHeight: 1.3 } }}
                            />
                          );
                        })()}
                      </Box>
                      <Typography variant="body2" color="black">
                        {employee.employee_code} • {employee.employee_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{employee.form_name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                      <Typography variant="body2" color="black">
                        {formatDate(employee.assigned_date)}
                      </Typography>
                      {employee.accepted_date && (
                        <Typography variant="body2" color="black">
                          Accepted: {formatDate(employee.accepted_date)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Chip 
                          label={getPeriodShortLabel(employee.review_period)} 
                          size="small" 
                          color="info" 
                          variant="outlined"
                        />
                        <Typography variant="body2" color="black">
                          End: {formatDate(employee.period_end_date)}
                        </Typography>
                      </Box>
                      {employee.days_remaining > 0 && !employee.is_period_expired && (
                        <Chip 
                          label={`${employee.days_remaining}d left`}
                          size="small"
                          color={getPeriodDaysWarningColor(employee.days_remaining)}
                        />
                      )}
                      {employee.is_period_expired && (
                        <Chip label="Expired" size="small" color="error" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{getDualManagerStatusChip(employee)}</TableCell>
                  
                  {/* Self Score */}
                  <TableCell>
                    {employee.self_review_overall_rating != null ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: "600", color: "info.main", mb: 0.25 }}>
                          {Number(employee.self_review_overall_rating).toFixed(2)}/5.00
                        </Typography>
                        <Typography variant="body2" sx={{ color: "info.main", fontWeight: "600" }}>
                          {((Number(employee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="black">
                        Not rated
                      </Typography>
                    )}
                  </TableCell>
                  
                  {/* Manager Score */}
                  <TableCell>
                    {(() => {
                      const rating = getEffectiveManagerRating(employee);
                      const isDualSame =
                        employee.manager_1 &&
                        employee.manager_2 &&
                        employee.manager_1 === employee.manager_2 &&
                        employee.manager_1_overall_rating != null &&
                        employee.manager_2_overall_rating != null;
                      return rating != null ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: "600", color: "primary.main", mb: 0.25 }}>
                            {rating.toFixed(2)}/5.00
                          </Typography>
                          <Typography variant="body2" sx={{ color: "primary.main", fontWeight: "600" }}>
                            {((rating / 5) * 100).toFixed(1)}%
                          </Typography>
                          {isDualSame && (
                            <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                              Avg of M1 &amp; M2
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="black">
                          Not rated
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  
                  <TableCell align="center">
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                      {/* Eye button — always visible, shows KRA structure only */}
                      <Tooltip title="View KRA Structure">
                        <IconButton
                          size="small"
                          onClick={() => handleViewStructureClick(employee)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {/* Pencil button — opens the review / edit dialog */}
                      <Tooltip
                        title={
                          employee.status === "pending"
                            ? "Pending Acceptance"
                            : employee.status === "accepted"
                            ? "Awaiting Self-Review"
                            : hasCurrentManagerSubmitted(employee)
                            ? "View / Edit Review"
                            : "Start Review"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleReviewClick(employee, "full")}
                            disabled={employee.status === "pending" || employee.status === "accepted"}
                            sx={{
                              ...(employee.status === "awaiting_manager_review" && !hasCurrentManagerSubmitted(employee) && {
                                backgroundColor: "primary.main",
                                color: "white",
                                "&:hover": { backgroundColor: "primary.dark" },
                              }),
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>

                {/* Expandable M1 / M2 detail row */}
                {hasDualManagers(employee) && (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedRows.has(employee.employee_kra_id)} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: "#f9f9f9",
                            borderBottom: "1px solid rgba(224,224,224,1)",
                          }}
                        >
                          <Grid container spacing={2}>
                            {/* M1 Panel */}
                            <Grid item xs={12} md={6}>
                              <Box
                                sx={{
                                  p: 2,
                                  border: "1.5px solid",
                                  borderColor: "primary.main",
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  backgroundColor: "#fff",
                                  gap: 2,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}
                                  >
                                    Manager Rating (M1)
                                    {employee.m1_project_name && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{ ml: 0.75, color: "text.secondary", fontWeight: 400 }}
                                      >
                                        · {employee.m1_project_name}
                                      </Typography>
                                    )}
                                  </Typography>
                                  {employee.manager_1_overall_rating != null ? (
                                    <>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: "700", color: "primary.main", lineHeight: 1.3 }}
                                      >
                                        {Number(employee.manager_1_overall_rating).toFixed(2)}/5.00
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 600 }}>
                                        {((Number(employee.manager_1_overall_rating) / 5) * 100).toFixed(1)}%
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                      Not yet submitted
                                    </Typography>
                                  )}
                                </Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  disabled={
                                    employee.status === "pending" || employee.status === "accepted"
                                  }
                                  onClick={() => handleReviewClick(employee, "m1")}
                                >
                                  view
                                </Button>
                              </Box>
                            </Grid>

                            {/* M2 Panel */}
                            <Grid item xs={12} md={6}>
                              <Box
                                sx={{
                                  p: 2,
                                  border: "1.5px solid",
                                  borderColor: "secondary.main",
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  backgroundColor: "#fff",
                                  gap: 2,
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: "700", color: "secondary.main", mb: 0.5 }}
                                  >
                                    Manager Rating (M2)
                                    {employee.m2_project_name && (
                                      <Typography
                                        component="span"
                                        variant="caption"
                                        sx={{ ml: 0.75, color: "text.secondary", fontWeight: 400 }}
                                      >
                                        · {employee.m2_project_name}
                                      </Typography>
                                    )}
                                  </Typography>
                                  {employee.manager_2_overall_rating != null ? (
                                    <>
                                      <Typography
                                        variant="body1"
                                        sx={{ fontWeight: "700", color: "secondary.main", lineHeight: 1.3 }}
                                      >
                                        {Number(employee.manager_2_overall_rating).toFixed(2)}/5.00
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "secondary.main", fontWeight: 600 }}>
                                        {((Number(employee.manager_2_overall_rating) / 5) * 100).toFixed(1)}%
                                      </Typography>
                                    </>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                      Not yet submitted
                                    </Typography>
                                  )}
                                </Box>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  disabled={
                                    employee.status === "pending" || employee.status === "accepted"
                                  }
                                  onClick={() => handleReviewClick(employee, "m2")}
                                >
                                  view
                                </Button>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* KRA Structure View Dialog (eye button) */}
      <Dialog
        open={structureDialogOpen}
        onClose={() => setStructureDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {structureEmployee?.form_name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {structureEmployee?.employee_name} &nbsp;·&nbsp; {structureEmployee?.designation}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {structureEmployee && structureEmployee.sections.map((section, sIdx) => (
            <Accordion key={sIdx} defaultExpanded elevation={1} sx={{ mb: 1, "&:before": { display: "none" }, borderLeft: "4px solid", borderColor: "primary.main", borderRadius: "4px !important" }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "grey.50", borderRadius: "4px" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", pr: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "700" }}>
                    {sIdx + 1}. {section.section}
                  </Typography>
                  <Chip
                    label={`${section.weightage}%`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1, pb: 1.5 }}>
                {section.questions.map((kpi, qIdx) => (
                  <Box
                    key={qIdx}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      py: 1,
                      borderBottom: qIdx < section.questions.length - 1 ? "1px solid" : "none",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, pr: 2 }}>
                      {qIdx + 1}. {kpi.question}
                    </Typography>
                    <Chip
                      label={`${kpi.weightage}%`}
                      size="small"
                      variant="outlined"
                      sx={{ flexShrink: 0 }}
                    />
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStructureDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => !submitting && !savingDraft && setReviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  Review KRA - {selectedEmployee?.employee_name}
                </Typography>
                {/* Mode badge — when opened from M1/M2 expand panel show a clear indicator */}
                {dialogMode === "m1" ? (
                  <Chip
                    label={selectedEmployee?.m1_project_name ? `M1 Review · ${selectedEmployee.m1_project_name}` : "M1 Review"}
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: "0.65rem", fontWeight: "bold", "& .MuiChip-label": { px: 1, lineHeight: 1.3 } }}
                  />
                ) : dialogMode === "m2" ? (
                  <Chip
                    label={selectedEmployee?.m2_project_name ? `M2 Review · ${selectedEmployee.m2_project_name}` : "M2 Review"}
                    size="small"
                    color="secondary"
                    sx={{ height: 20, fontSize: "0.65rem", fontWeight: "bold", "& .MuiChip-label": { px: 1, lineHeight: 1.3 } }}
                  />
                ) : selectedEmployee && (() => {
                  if (isCompletedDualRole(selectedEmployee)) {
                    return (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        <Chip
                          label={selectedEmployee.m1_project_name ? `M1 · ${selectedEmployee.m1_project_name}` : "M1"}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem", fontWeight: "bold", "& .MuiChip-label": { px: 1, lineHeight: 1.3 } }}
                        />
                        <Chip
                          label={selectedEmployee.m2_project_name ? `M2 · ${selectedEmployee.m2_project_name}` : "M2"}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem", fontWeight: "bold", "& .MuiChip-label": { px: 1, lineHeight: 1.3 } }}
                        />
                      </Box>
                    );
                  }
                  const level = getManagerLevel(selectedEmployee);
                  if (!level) return null;
                  const isDualRole =
                    selectedEmployee.manager_1 &&
                    selectedEmployee.manager_2 &&
                    selectedEmployee.manager_1 === selectedEmployee.manager_2 &&
                    selectedEmployee.manager_1 === currentUserId;
                  const projectLabel =
                    isDualRole
                      ? level === "M1"
                        ? selectedEmployee.m1_project_name
                        : selectedEmployee.m2_project_name
                      : null;
                  return (
                    <Chip
                      label={
                        projectLabel
                          ? `Reviewing as ${level} (${projectLabel})`
                          : `You are ${level}`
                      }
                      size="small"
                      color={level === "M1" ? "primary" : "secondary"}
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.65rem", fontWeight: "bold", "& .MuiChip-label": { px: 1, lineHeight: 1.3 } }}
                    />
                  );
                })()}
              </Box>
              <Typography variant="body2" color="black">
                {selectedEmployee?.form_name}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedEmployee && (
            <Box>

              {/* ── M1 read-only view ─────────────────────────────────────── */}
              {dialogMode === "m1" && (() => {
                const m1Submitted = !!selectedEmployee.manager_1_review_submitted_date;
                return (
                  <Box>
                    {!m1Submitted && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        M1 review has not been submitted yet.
                      </Alert>
                    )}
                    {selectedEmployee.sections.map((section, sectionIndex) => (
                      <Accordion key={sectionIndex} defaultExpanded elevation={0} sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "grey.50" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pr: 2 }}>
                            <Typography sx={{ fontWeight: "600" }}>
                              {sectionIndex + 1}. {section.section}
                            </Typography>
                            <Typography sx={{ color: "primary.main", fontWeight: "600" }}>
                              {section.weightage}%
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {section.questions.map((kpi, questionIndex) => {
                              const m1Rating = kpi.m1_manager_rating ?? kpi.manager_rating;
                              const m1Comments = kpi.m1_manager_comments ?? kpi.manager_comments;
                              return (
                                <Box key={questionIndex}>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                    <Typography variant="body2" sx={{ flex: 1, fontWeight: "bold", fontSize: "15px" }}>
                                      {kpi.question}
                                    </Typography>
                                    <Chip label={`${kpi.weightage}%`} size="small" color="primary" sx={{ ml: 2 }} />
                                  </Box>
                                  <Box sx={{ mt: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 1, border: "1px solid #e0e0e0" }}>
                                    {kpi.self_rating != null && (
                                      <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                                        <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main", display: "block", mb: 0.5 }}>
                                          Employee Self-Rating:
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                          <Typography variant="h6" sx={{ fontWeight: "700", color: "info.main" }}>
                                            {formatRatingLabel(kpi.self_rating, kpi.rating_type)}{kpi.rating_type === "numerical" || !kpi.rating_type ? "/5.00" : ""}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: "info.main", fontWeight: "600" }}>
                                            {((kpi.self_rating / 5) * 100).toFixed(1)}%
                                          </Typography>
                                        </Box>
                                        {kpi.self_comments && (
                                          <Box sx={{ mt: 0.5 }}>
                                            <RichTextDisplay content={kpi.self_comments} />
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                    <Box sx={{ p: 1.5, backgroundColor: "#e8eaf6", borderRadius: 1, border: "1px solid #9fa8da" }}>
                                      <Typography variant="caption" sx={{ fontWeight: "700", color: "primary.main", display: "block", mb: 0.5 }}>
                                        M1 Rating:
                                      </Typography>
                                      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                        <Typography variant="h6" sx={{ fontWeight: "700", color: "primary.main" }}>
                                          {m1Rating != null
                                            ? `${formatRatingLabel(m1Rating, kpi.rating_type)}${kpi.rating_type === "numerical" || !kpi.rating_type ? "/5.00" : ""}`
                                            : "Not Rated"}
                                        </Typography>
                                        {m1Rating != null && (
                                          <Typography variant="body2" sx={{ color: "primary.main", fontWeight: "600" }}>
                                            {((m1Rating / 5) * 100).toFixed(1)}%
                                          </Typography>
                                        )}
                                      </Box>
                                      {m1Comments && (
                                        <Box sx={{ mt: 1, p: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.25 }}>
                                            Comments:
                                          </Typography>
                                          <RichTextDisplay content={m1Comments} />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                  {questionIndex < section.questions.length - 1 && <Divider sx={{ mt: 3 }} />}
                                </Box>
                              );
                            })}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    <Divider sx={{ my: 3 }} />
                    {/* M1 overall feedback */}
                    {selectedEmployee.manager_1_overall_comments && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5 }}>
                          🗒️ M1 Manager Overall Feedback
                        </Typography>
                        <Box sx={{ p: 2, borderRadius: 1, border: "2px solid", borderColor: "primary.main" }}>
                          <RichTextDisplay content={selectedEmployee.manager_1_overall_comments} />
                        </Box>
                      </Box>
                    )}
                    {/* M1 overall rating summary */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {selectedEmployee.self_review_overall_rating != null && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5 }}>Employee Self-Rating</Typography>
                          <Box sx={{ p: 2, backgroundColor: "info.lighter", borderRadius: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                              {Number(selectedEmployee.self_review_overall_rating).toFixed(2)}/5.00
                            </Typography>
                            <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                              {((Number(selectedEmployee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12} md={selectedEmployee.self_review_overall_rating != null ? 6 : 12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5 }}>M1 Overall Rating</Typography>
                        <Box sx={{ p: 2, backgroundColor: "primary.lighter", borderRadius: 1 }}>
                          {selectedEmployee.manager_1_overall_rating != null ? (
                            <>
                              <Typography variant="h4" sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}>
                                {Number(selectedEmployee.manager_1_overall_rating).toFixed(2)}/5.00
                              </Typography>
                              <Typography variant="h6" sx={{ color: "primary.main", fontWeight: "600" }}>
                                {((Number(selectedEmployee.manager_1_overall_rating) / 5) * 100).toFixed(1)}%
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body1" color="text.secondary">Not submitted</Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })()}

              {/* ── M2 read-only view (only when M2 is already submitted) ─── */}
              {dialogMode === "m2" && !!selectedEmployee.manager_2_review_submitted_date && (() => {
                return (
                  <Box>
                    {selectedEmployee.sections.map((section, sectionIndex) => (
                      <Accordion key={sectionIndex} defaultExpanded elevation={0} sx={{ mb: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: "grey.50" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", pr: 2 }}>
                            <Typography sx={{ fontWeight: "600" }}>
                              {sectionIndex + 1}. {section.section}
                            </Typography>
                            <Typography sx={{ color: "secondary.main", fontWeight: "600" }}>
                              {section.weightage}%
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {section.questions.map((kpi, questionIndex) => {
                              const m2Rating = kpi.m2_manager_rating ?? kpi.manager_rating;
                              const m2Comments = kpi.m2_manager_comments ?? kpi.manager_comments;
                              return (
                                <Box key={questionIndex}>
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                                    <Typography variant="body2" sx={{ flex: 1, fontWeight: "bold", fontSize: "15px" }}>
                                      {kpi.question}
                                    </Typography>
                                    <Chip label={`${kpi.weightage}%`} size="small" color="secondary" sx={{ ml: 2 }} />
                                  </Box>
                                  <Box sx={{ mt: 2, p: 2, backgroundColor: "#f8f9fa", borderRadius: 1, border: "1px solid #e0e0e0" }}>
                                    {kpi.self_rating != null && (
                                      <Box sx={{ mb: 2, p: 1.5, backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                                        <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main", display: "block", mb: 0.5 }}>
                                          Employee Self-Rating:
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                          <Typography variant="h6" sx={{ fontWeight: "700", color: "info.main" }}>
                                            {formatRatingLabel(kpi.self_rating, kpi.rating_type)}{kpi.rating_type === "numerical" || !kpi.rating_type ? "/5.00" : ""}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: "info.main", fontWeight: "600" }}>
                                            {((kpi.self_rating / 5) * 100).toFixed(1)}%
                                          </Typography>
                                        </Box>
                                        {kpi.self_comments && (
                                          <Box sx={{ mt: 0.5 }}>
                                            <RichTextDisplay content={kpi.self_comments} />
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                    <Box sx={{ p: 1.5, backgroundColor: "#f3e5f5", borderRadius: 1, border: "1px solid #ce93d8" }}>
                                      <Typography variant="caption" sx={{ fontWeight: "700", color: "secondary.main", display: "block", mb: 0.5 }}>
                                        M2 Rating:
                                      </Typography>
                                      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                        <Typography variant="h6" sx={{ fontWeight: "700", color: "secondary.main" }}>
                                          {m2Rating != null
                                            ? `${formatRatingLabel(m2Rating, kpi.rating_type)}${kpi.rating_type === "numerical" || !kpi.rating_type ? "/5.00" : ""}`
                                            : "Not Rated"}
                                        </Typography>
                                        {m2Rating != null && (
                                          <Typography variant="body2" sx={{ color: "secondary.main", fontWeight: "600" }}>
                                            {((m2Rating / 5) * 100).toFixed(1)}%
                                          </Typography>
                                        )}
                                      </Box>
                                      {m2Comments && (
                                        <Box sx={{ mt: 1, p: 1, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.25 }}>
                                            Comments:
                                          </Typography>
                                          <RichTextDisplay content={m2Comments} />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                  {questionIndex < section.questions.length - 1 && <Divider sx={{ mt: 3 }} />}
                                </Box>
                              );
                            })}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    <Divider sx={{ my: 3 }} />
                    {/* M2 overall feedback */}
                    {selectedEmployee.manager_2_overall_comments && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5 }}>
                          🗒️ M2 Manager Overall Feedback
                        </Typography>
                        <Box sx={{ p: 2, borderRadius: 1, border: "2px solid", borderColor: "secondary.main" }}>
                          <RichTextDisplay content={selectedEmployee.manager_2_overall_comments} />
                        </Box>
                      </Box>
                    )}
                    {/* M2 overall rating summary */}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      {selectedEmployee.self_review_overall_rating != null && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5 }}>Employee Self-Rating</Typography>
                          <Box sx={{ p: 2, backgroundColor: "info.lighter", borderRadius: 1 }}>
                            <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                              {Number(selectedEmployee.self_review_overall_rating).toFixed(2)}/5.00
                            </Typography>
                            <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                              {((Number(selectedEmployee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12} md={selectedEmployee.self_review_overall_rating != null ? 6 : 12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5 }}>M2 Overall Rating</Typography>
                        <Box sx={{ p: 2, backgroundColor: "secondary.lighter", borderRadius: 1 }}>
                          {selectedEmployee.manager_2_overall_rating != null ? (
                            <>
                              <Typography variant="h4" sx={{ fontWeight: "700", color: "secondary.main", mb: 0.5 }}>
                                {Number(selectedEmployee.manager_2_overall_rating).toFixed(2)}/5.00
                              </Typography>
                              <Typography variant="h6" sx={{ color: "secondary.main", fontWeight: "600" }}>
                                {((Number(selectedEmployee.manager_2_overall_rating) / 5) * 100).toFixed(1)}%
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body1" color="text.secondary">Not submitted</Typography>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                );
              })()}

              {/* ── Full combined view (eye-button) ──────────────────────── */}
              {(dialogMode === "full" || (dialogMode === "m2" && !selectedEmployee.manager_2_review_submitted_date)) && (
              <Box>
              {/* KRA Sections */}
              {selectedEmployee.sections.map((section, sectionIndex) => (
                <Accordion key={sectionIndex} defaultExpanded elevation={0} sx={{ mb: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ backgroundColor: "grey.50" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        pr: 2,
                      }}
                    >
                      <Typography sx={{ fontWeight: "600" }}>
                        {sectionIndex + 1}. {section.section}
                      </Typography>
                      <Typography sx={{ color: "primary.main", fontWeight: "600" }}>
                        {section.weightage}%
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {section.questions.map((kpi, questionIndex) => (
                        <Box key={questionIndex}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Typography variant="body2" sx={{ flex: 1, fontWeight: "bold", fontSize: "15px" }}>
                              {kpi.question}
                            </Typography>
                            <Chip
                              label={`${kpi.weightage}%`}
                              size="small"
                              color="primary"
                              sx={{ ml: 2 }}
                            />
                          </Box>
                          
                          {/* Completed dual-role read-only view: show M1 and M2 together */}
                          {isCompletedDualRole(selectedEmployee) && hasCurrentManagerSubmitted(selectedEmployee) ? (
                            <Box sx={{ mt: 2, p: 2.5, backgroundColor: "#f8f9fa", borderRadius: 1, border: "2px solid #e0e0e0" }}>
                              {/* Employee self-rating */}
                              {kpi.self_rating != null && (
                                <Box sx={{ mb: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                                  <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main", display: "block", mb: 0.5 }}>
                                    Employee Self-Rating:
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                    <Typography variant="h5" sx={{ fontWeight: "700", color: "info.main" }}>
                                      {formatRatingLabel(kpi.self_rating, kpi.rating_type)}{kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "info.main", fontWeight: "600" }}>
                                      {((kpi.self_rating / 5) * 100).toFixed(1)}%
                                    </Typography>
                                  </Box>
                                  {kpi.self_comments && (
                                    <Box sx={{ mt: 1 }}>
                                      <RichTextDisplay content={kpi.self_comments} />
                                    </Box>
                                  )}
                                </Box>
                              )}

                              {/* M1 rating & comments */}
                              <Box sx={{ mb: 2, p: 2, backgroundColor: "#e8eaf6", borderRadius: 1, border: "1px solid #9fa8da" }}>
                                <Typography variant="caption" sx={{ fontWeight: "700", color: "primary.main", display: "block", mb: 1 }}>
                                  Your Rating as M1{selectedEmployee.m1_project_name ? ` · ${selectedEmployee.m1_project_name}` : ""}:
                                </Typography>
                                <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 0.5 }}>
                                  <Typography variant="h5" sx={{ fontWeight: "700", color: "primary.main" }}>
                                    {kpi.m1_manager_rating != null
                                      ? `${formatRatingLabel(kpi.m1_manager_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}`
                                      : 'Not Rated'}
                                  </Typography>
                                  {kpi.m1_manager_rating != null && kpi.m1_manager_rating > 0 && (
                                    <Typography variant="body2" sx={{ color: "primary.main", fontWeight: "600" }}>
                                      {((kpi.m1_manager_rating / 5) * 100).toFixed(1)}%
                                    </Typography>
                                  )}
                                </Box>
                                {kpi.m1_manager_comments && (
                                  <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "#e8f5e9", borderRadius: 1, border: "1px solid #a5d6a7" }}>
                                    <Typography variant="caption" sx={{ fontWeight: "600", color: "success.main", display: "block", mb: 0.5 }}>
                                      Your Comments (M1):
                                    </Typography>
                                    <RichTextDisplay content={kpi.m1_manager_comments} />
                                  </Box>
                                )}
                              </Box>

                              {/* M2 rating & comments */}
                              <Box sx={{ p: 2, backgroundColor: "#f3e5f5", borderRadius: 1, border: "1px solid #ce93d8" }}>
                                <Typography variant="caption" sx={{ fontWeight: "700", color: "secondary.main", display: "block", mb: 1 }}>
                                  Your Rating as M2{selectedEmployee.m2_project_name ? ` · ${selectedEmployee.m2_project_name}` : ""}:
                                </Typography>
                                <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 0.5 }}>
                                  <Typography variant="h5" sx={{ fontWeight: "700", color: "secondary.main" }}>
                                    {kpi.m2_manager_rating != null
                                      ? `${formatRatingLabel(kpi.m2_manager_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}`
                                      : 'Not Rated'}
                                  </Typography>
                                  {kpi.m2_manager_rating != null && kpi.m2_manager_rating > 0 && (
                                    <Typography variant="body2" sx={{ color: "secondary.main", fontWeight: "600" }}>
                                      {((kpi.m2_manager_rating / 5) * 100).toFixed(1)}%
                                    </Typography>
                                  )}
                                </Box>
                                {kpi.m2_manager_comments && (
                                  <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "#e8f5e9", borderRadius: 1, border: "1px solid #a5d6a7" }}>
                                    <Typography variant="caption" sx={{ fontWeight: "600", color: "success.main", display: "block", mb: 0.5 }}>
                                      Your Comments (M2):
                                    </Typography>
                                    <RichTextDisplay content={kpi.m2_manager_comments} />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          ) : /* Employee Self-Rating and Manager Rating */
                          kpi.self_rating != null && typeof kpi.self_rating === 'number' ? (
                            <Box sx={{ mt: 2, p: 2.5, backgroundColor: "#f1f8f4", borderRadius: 1, border: "2px solid #81c784" }}>
                              {/* Employee Self-Rating row */}
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: "600", display: "block", mb: 0.5, color: "#000" }}>
                                  Employee Self-Rating:
                                </Typography>
                                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                  <Typography variant="h5" sx={{ fontWeight: "700", color: "info.main" }}>
                                    {formatRatingLabel(kpi.self_rating, kpi.rating_type)}{kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "info.main", fontWeight: "600" }}>
                                    {((kpi.self_rating / 5) * 100).toFixed(1)}%
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Manager Rating — full width so radio buttons don't overflow */}
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: "600", display: "block", mb: 1, color: "#000" }}>
                                  Manager Rating:
                                </Typography>
                                <Box sx={{ mb: 1.5 }}>
                                  <RatingInput
                                    ratingType={kpi.rating_type}
                                    value={kpi.manager_rating}
                                    onChange={(value) => handleRatingChange(sectionIndex, questionIndex, value)}
                                    disabled={hasCurrentManagerSubmitted(selectedEmployee)}
                                  />
                                </Box>
                                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                                  <Typography variant="h5" sx={{ fontWeight: "700", color: "primary.main" }}>
                                    {kpi.manager_rating !== null && kpi.manager_rating !== undefined ? `${formatRatingLabel(kpi.manager_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}` : 'Not Rated'}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "success.main", fontWeight: "600" }}>
                                    {kpi.manager_rating && kpi.manager_rating > 0 ? `${((kpi.manager_rating / 5) * 100).toFixed(1)}%` : '0.0%'}
                                  </Typography>
                                </Box>
                              </Box>

                              {/* Employee Self-Comments (Read-only) */}
                              {kpi.self_comments && (
                                <Box sx={{ mt: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                                  <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main", display: "block", mb: 1 }}>
                                    Employee's Comments:
                                  </Typography>
                                  <RichTextDisplay content={kpi.self_comments} />
                                </Box>
                              )}

                              {/* Manager Comment for this KPI */}
                              {hasCurrentManagerSubmitted(selectedEmployee) ? (
                                kpi.manager_comments ? (
                                  <Box sx={{ mt: 2, p: 2, backgroundColor: "#e8f5e9", borderRadius: 1, border: "1px solid #a5d6a7" }}>
                                    <Typography variant="caption" sx={{ fontWeight: "600", color: "success.main", display: "block", mb: 1 }}>
                                      Your Comments for this KPI:
                                    </Typography>
                                    <RichTextDisplay content={kpi.manager_comments} />
                                  </Box>
                                ) : null
                              ) : (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                                    Your Comments for this KPI *
                                  </Typography>
                                  <SkillDocumentCommentBox
                                    value={kpi.manager_comments || ""}
                                    onChange={(val) => handleKPICommentChange(sectionIndex, questionIndex, val)}
                                    placeholder="Provide specific feedback on this KPI performance..."
                                    disabled={submitting}
                                    compact
                                    hideTableControls
                                  />
                                  {showKPICommentsError && isRichTextEmpty(kpi.manager_comments) && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                                      Comment is required
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              
                            </Box>
                          ) : (
                            // If no self-rating, show manager rating separately
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="subtitle2" sx={{ mb: 1.5, display: "block", fontWeight: "600" }}>
                                Manager Rating:
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                <RatingInput
                                  ratingType={kpi.rating_type}
                                  value={kpi.manager_rating}
                                  onChange={(value) => handleRatingChange(sectionIndex, questionIndex, value)}
                                  disabled={hasCurrentManagerSubmitted(selectedEmployee)}
                                />
                              </Box>
                              <Box>
                                <Typography variant="h5" sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}>
                                  {kpi.manager_rating !== null && kpi.manager_rating !== undefined ? `${formatRatingLabel(kpi.manager_rating, kpi.rating_type)}${kpi.rating_type === 'numerical' || !kpi.rating_type ? '/5.00' : ''}` : 'Not Rated'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "success.main", fontWeight: "600" }}>
                                  {kpi.manager_rating && kpi.manager_rating > 0 ? `${((kpi.manager_rating / 5) * 100).toFixed(1)}%` : '0.0%'}
                                </Typography>
                              </Box>
                              
                              {/* Employee Self-Comments (Read-only) */}
                              {kpi.self_comments && (
                                <Box sx={{ mt: 2, p: 2, backgroundColor: "#e3f2fd", borderRadius: 1, border: "1px solid #90caf9" }}>
                                  <Typography variant="caption" sx={{ fontWeight: "600", color: "info.main", display: "block", mb: 1 }}>
                                    Employee's Comments:
                                  </Typography>
                                  <RichTextDisplay content={kpi.self_comments} />
                                </Box>
                              )}

                              {/* Manager Comment for this KPI */}
                              {hasCurrentManagerSubmitted(selectedEmployee) ? (
                                kpi.manager_comments ? (
                                  <Box sx={{ mt: 2, p: 2, backgroundColor: "#e8f5e9", borderRadius: 1, border: "1px solid #a5d6a7" }}>
                                    <Typography variant="caption" sx={{ fontWeight: "600", color: "success.main", display: "block", mb: 1 }}>
                                      Your Comments for this KPI:
                                    </Typography>
                                    <RichTextDisplay content={kpi.manager_comments} />
                                  </Box>
                                ) : null
                              ) : (
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="caption" sx={{ mb: 1, display: "block" }}>
                                    Your Comments for this KPI *
                                  </Typography>
                                  <SkillDocumentCommentBox
                                    value={kpi.manager_comments || ""}
                                    onChange={(val) => handleKPICommentChange(sectionIndex, questionIndex, val)}
                                    placeholder="Provide specific feedback on this KPI performance..."
                                    disabled={submitting}
                                    compact
                                    hideTableControls
                                  />
                                  {showKPICommentsError && isRichTextEmpty(kpi.manager_comments) && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                                      Comment is required
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                          {questionIndex < section.questions.length - 1 && (
                            <Divider sx={{ mt: 3 }} />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}

              <Divider sx={{ my: 3 }} />

              {/* Display Employee's Overall Comments if provided */}
              {selectedEmployee.self_review_overall_comments && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                    💭 Employee's Overall Comments
                  </Typography>
                  <Box sx={{ p: 2, backgroundColor: "#e3f2fd", borderRadius: 1 }}>
                    <RichTextDisplay content={selectedEmployee.self_review_overall_comments} />
                  </Box>
                </Box>
              )}

              {/* Manager Overall Feedback */}
              <Box sx={{ mb: 3 }}>
                {isCompletedDualRole(selectedEmployee) && hasCurrentManagerSubmitted(selectedEmployee) ? (
                  /* Completed dual-role: show M1 and M2 feedback side by side */
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      🗒️ Your Manager Feedback (Both Roles)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, borderRadius: 1, border: "2px solid", borderColor: "primary.main", height: "100%" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: "700", mb: 1.5, color: "primary.main" }}>
                            As M1{selectedEmployee.m1_project_name ? ` · ${selectedEmployee.m1_project_name}` : ""}
                          </Typography>
                          {selectedEmployee.manager_1_overall_rating != null && (
                            <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: "primary.lighter", borderRadius: 1 }}>
                              <Typography variant="h5" sx={{ fontWeight: "700", color: "primary.main", mb: 0.25 }}>
                                {Number(selectedEmployee.manager_1_overall_rating).toFixed(2)}/5.00
                              </Typography>
                              <Typography variant="body2" sx={{ color: "primary.main", fontWeight: "600" }}>
                                {((Number(selectedEmployee.manager_1_overall_rating) / 5) * 100).toFixed(1)}%
                              </Typography>
                            </Box>
                          )}
                          <RichTextDisplay content={selectedEmployee.manager_1_overall_comments || ""} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, borderRadius: 1, border: "2px solid", borderColor: "secondary.main", height: "100%" }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: "700", mb: 1.5, color: "secondary.main" }}>
                            As M2{selectedEmployee.m2_project_name ? ` · ${selectedEmployee.m2_project_name}` : ""}
                          </Typography>
                          {selectedEmployee.manager_2_overall_rating != null && (
                            <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: "secondary.lighter", borderRadius: 1 }}>
                              <Typography variant="h5" sx={{ fontWeight: "700", color: "secondary.main", mb: 0.25 }}>
                                {Number(selectedEmployee.manager_2_overall_rating).toFixed(2)}/5.00
                              </Typography>
                              <Typography variant="body2" sx={{ color: "secondary.main", fontWeight: "600" }}>
                                {((Number(selectedEmployee.manager_2_overall_rating) / 5) * 100).toFixed(1)}%
                              </Typography>
                            </Box>
                          )}
                          <RichTextDisplay content={selectedEmployee.manager_2_overall_comments || ""} />
                        </Box>
                      </Grid>
                    </Grid>
                  </>
                ) : hasCurrentManagerSubmitted(selectedEmployee) ? (
                  <Box sx={{ mb: 2, p: 2, borderRadius: 1, border: "2px solid black" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "700", mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                      🗒️ Manager Overall Feedback
                    </Typography>
                    <RichTextDisplay content={selectedEmployee.manager_overall_comments || ""} />
                  </Box>
                ) : (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      Manager Overall Feedback
                    </Typography>
                    <SkillDocumentCommentBox
                      value={selectedEmployee.manager_overall_comments || ""}
                      onChange={(val) => handleOverallCommentsChange(val)}
                      placeholder="Provide comprehensive feedback on the employee's overall performance, strengths, areas for improvement, and recommendations..."
                      disabled={submitting}
                      compact
                    />
                    {showCommentsError && isRichTextEmpty(selectedEmployee.manager_overall_comments) && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                        Overall comments are required
                      </Typography>
                    )}
                  </>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Employee and Manager Ratings Side by Side */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* Employee Self-Rating */}
                {selectedEmployee.self_review_overall_rating != null && (
                  <Grid item xs={12} md={isCompletedDualRole(selectedEmployee) && hasCurrentManagerSubmitted(selectedEmployee) ? 4 : 6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      Employee Self-Rating
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "info.lighter", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                        {Number(selectedEmployee.self_review_overall_rating).toFixed(2)}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                        {((Number(selectedEmployee.self_review_overall_rating) / 5) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {isCompletedDualRole(selectedEmployee) && hasCurrentManagerSubmitted(selectedEmployee) ? (
                  /* Completed dual-role: show M1 and M2 ratings separately */
                  <>
                    <Grid item xs={12} md={selectedEmployee.self_review_overall_rating != null ? 4 : 6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                        M1 Rating{selectedEmployee.m1_project_name ? ` · ${selectedEmployee.m1_project_name}` : ""}
                      </Typography>
                      <Box sx={{ p: 2, backgroundColor: "primary.lighter", borderRadius: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}>
                          {selectedEmployee.manager_1_overall_rating != null
                            ? `${Number(selectedEmployee.manager_1_overall_rating).toFixed(2)}/5.00`
                            : "—"}
                        </Typography>
                        <Typography variant="h6" sx={{ color: "primary.main", fontWeight: "600" }}>
                          {selectedEmployee.manager_1_overall_rating != null
                            ? `${((Number(selectedEmployee.manager_1_overall_rating) / 5) * 100).toFixed(1)}%`
                            : ""}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={selectedEmployee.self_review_overall_rating != null ? 4 : 6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                        M2 Rating{selectedEmployee.m2_project_name ? ` · ${selectedEmployee.m2_project_name}` : ""}
                      </Typography>
                      <Box sx={{ p: 2, backgroundColor: "secondary.lighter", borderRadius: 1 }}>
                        <Typography variant="h4" sx={{ fontWeight: "700", color: "secondary.main", mb: 0.5 }}>
                          {selectedEmployee.manager_2_overall_rating != null
                            ? `${Number(selectedEmployee.manager_2_overall_rating).toFixed(2)}/5.00`
                            : "—"}
                        </Typography>
                        <Typography variant="h6" sx={{ color: "secondary.main", fontWeight: "600" }}>
                          {selectedEmployee.manager_2_overall_rating != null
                            ? `${((Number(selectedEmployee.manager_2_overall_rating) / 5) * 100).toFixed(1)}%`
                            : ""}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                ) : (
                  /* Normal: single manager rating */
                  <Grid item xs={12} md={selectedEmployee.self_review_overall_rating != null ? 6 : 12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1.5, color: "text.primary" }}>
                      Manager Rating
                    </Typography>
                    <Box sx={{ p: 2, backgroundColor: "primary.lighter", borderRadius: 1 }}>
                      <Typography variant="h4" sx={{ fontWeight: "700", color: "primary.main", mb: 0.5 }}>
                        {average}/5.00
                      </Typography>
                      <Typography variant="h6" sx={{ color: "success.main", fontWeight: "600" }}>
                        {percentage}%
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>

            </Box>
            )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {selectedEmployee && (
            dialogMode === "m1" ||
            (dialogMode === "m2" && !!selectedEmployee.manager_2_review_submitted_date) ||
            hasCurrentManagerSubmitted(selectedEmployee)
          ) ? (
            <Button onClick={() => setReviewDialogOpen(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={() => setReviewDialogOpen(false)} disabled={submitting || savingDraft}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={handleSaveDraft}
                disabled={submitting || savingDraft}
                startIcon={savingDraft ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {savingDraft ? "Saving..." : "Save Draft"}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitReview}
                disabled={submitting || savingDraft}
                startIcon={submitting ? <CircularProgress size={20} /> : null}
              >
                {submitting ? "Submitting..." : "Submit Review"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManagerKRAReview;
